import { apiIntegrations } from "@/api/integrations/integration.api.ts";
import { EmailSequenceRunDetails } from "@/components/email/EmailSequenceRunDetails.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Button,
  ConfirmationModal,
  HtmlViewer,
  ListItem,
  Stack,
} from "@skailer-com/uikit";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useEmailSequenceRunCancel } from "@/queries/recruit/use-candidates.query.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiEmail, ApiEmailSequenceRunStatus } from "@/types/integrations/integration.types.ts";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export interface EmailViewModalProps extends ModalProps {
  email: ApiEmail;
  onReply: () => void;
}

const formatEmails = (emails: string) => {
  return emails
    .split(",")
    .map((email) => email.trim())
    .join("\n");
};

export const EmailViewModal = ({ email, onReply, ...props }: EmailViewModalProps) => {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const cancelSequenceRun = useEmailSequenceRunCancel(email.candidate_id);
  const sequenceRun = cancelSequenceRun.data ?? email.sequence_run;

  const handleReply = () => {
    onReply();
    close(id);
  };

  const handleCancelSequence = () => {
    if (!sequenceRun || sequenceRun.status !== ApiEmailSequenceRunStatus.active) return;

    openModal(ConfirmationModal, {
      question: t("ats:email_sequence_cancel"),
      text: t("ats:email_sequence_cancel_confirmation"),
      acceptTitle: t("ats:email_sequence_cancel"),
      secondButtonTitle: t("common:close"),
      onAccept: async () => {
        try {
          await cancelSequenceRun.mutateAsync(sequenceRun.id);
        } catch {
          return;
        }
      },
    });
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.email.viewed, {
      [analyticProperties.id]: email.id,
    });

    apiIntegrations.emailMarkAsRead(email.id);
  });

  return (
    <Modal
      {...props}
      layout={"stretch"}
      size={"lg"}
      title={t("core:email_noun")}
      withCloser
      closeByEscEnabled
      actions={
        !email.is_outgoing ? (
          <Button onClick={handleReply} size={"lg"} className={"w-full"} type={"button"}>
            {t("common:reply")}
          </Button>
        ) : sequenceRun?.status === ApiEmailSequenceRunStatus.active ? (
          <Stack gap={"sm"}>
            <Button onClick={() => close(id)} size={"lg"} className={"w-full"} type={"button"}>
              {t("common:close")}
            </Button>
            <Button
              onClick={handleCancelSequence}
              isLoading={cancelSequenceRun.isPending}
              variant={"danger"}
              className={"w-full"}
            >
              {t("ats:email_sequence_cancel")}
            </Button>
          </Stack>
        ) : (
          <Button onClick={() => close(id)} size={"lg"} className={"w-full"} type={"button"}>
            {t("common:close")}
          </Button>
        )
      }
    >
      <Stack gap={"md"}>
        <Stack className={"w-full"}>
          <ListItem preTitle={t("core:email_from")} title={email.from_email} />
          {email.cc_email && (
            <ListItem preTitle={t("core:email_cc_email")} title={formatEmails(email.cc_email)} />
          )}
          {email.to_email && (
            <ListItem preTitle={t("core:email_to")} title={formatEmails(email.to_email)} />
          )}
          {email.bcc_email && (
            <ListItem preTitle={t("core:email_bcc_email")} title={formatEmails(email.bcc_email)} />
          )}
          {email.subject && <ListItem preTitle={t("core:email_subject")} title={email.subject} />}
        </Stack>
        <Stack gap="sm">
          {email.body && <HtmlViewer content={email.body} />}
          {email.files.map((file: ApiFile) => (
            <FileItem file={file} />
          ))}
        </Stack>
        {sequenceRun && (
          <EmailSequenceRunDetails message={{ ...email, sequence_run: sequenceRun }} />
        )}
      </Stack>
    </Modal>
  );
};
